import React, { useEffect } from "react";
import {
  Input, GooglePlacesAutoComplete, Button, Checkbox, Switcher,
  Select
} from "../..";
import { INVALID_ADDRESS_MESSAGE } from "../../../constants/constants";
import styles from "./index.module.css";
import UberCarIcon from '../../../assets/svgs/ubercar.svg';
import InsightsHOIcon from '../../../assets/svgs/insightsHO.svg';
import GeminiIcon from '../../../assets/svgs/gemini.svg';
import RideRequestFields from "../../common/RideRequestFields/RideRequestFields";
import { useSelector } from "react-redux";
import { useCurrentRole } from "../../../hooks/useCurrentRole";

export const CreateHO = ({
  personalInfoFieldsDisabled,
  switchAdmin,
  handleUpdateAdmin,
  values,
  handleSelectAddress,
  handleChangeAddress,
  handleChangeAdmin,
  handleAssignAdmin,
  handleInviteUser,
  parentOrgId,
  disableEnterpriseCheckbox,
  role,
  activeChildHOAdmins,
  ...rest
}) => {
  const user = useSelector((state) => state.authorization.user);
  const { isMHAdmin } = useCurrentRole(role);
  const isTopLevelOrg = !values?.parentOrgId;
  const isEnterpriseOrg = !!values?.isEnterprise;
  const shouldShowFeatures = isMHAdmin && isTopLevelOrg;
  const shouldShowEnterpriseOnlyFeatures = shouldShowFeatures && isEnterpriseOrg;
  const { validateForm } = rest;
  
  useEffect(() => {
    validateForm();
  }, [values?.location, values?.addons, validateForm]);

  const handleAdminChange = async ({ name, value, values, setFieldValue, validateForm }) => {
    await setFieldValue(name, value);
    const selectedAdmin = activeChildHOAdmins?.find((option) => option.fullName === value);
    
    await setFieldValue("admin.email", selectedAdmin.email);
    await setFieldValue("admin.firstName", selectedAdmin.firstName);
    await setFieldValue("admin.lastName", selectedAdmin.lastName);
    await setFieldValue("admin.adminId", selectedAdmin.id);
    await validateForm();
  };

  return (
    <>
      <label className={styles.name}>
        <span className={styles.required}>Company Name</span>
        <Input name="name" type="text" placeholder="Organization Name" />
      </label>
      <label className={styles.organizationLocation}>
        <span className={styles.required}>Address</span>
        <GooglePlacesAutoComplete
          name="location.formattedAddress"
          value={values?.location?.formattedAddress}
          handleSelectAddress={handleSelectAddress}
          handleChangeAddress={handleChangeAddress}
        />
        {rest.touched &&
          rest.touched["location.formattedAddress"] &&
          rest.errors.location && (
            <div className={styles.errorMessage}>{INVALID_ADDRESS_MESSAGE}</div>
          )}
      </label>
      {isTopLevelOrg && (
        <div className={styles.enterpriseContainer}>
          <Checkbox
            name="isEnterprise"
            checked={values?.isEnterprise}
            disabled={disableEnterpriseCheckbox}
            onChange={({ setFieldValue, name, checked }) => {
              setFieldValue(name, checked);
              // When toggling enterprise off, clear enterprise-only flags
              if (checked) {
                setFieldValue('addons', { insightsEnabled: true });
              } else {
                setFieldValue('addons', {});
              }
            }}
          />
          <span className={styles.enterpriseLabel}>Is Enterprise Organization</span>
        </div>
      )}

      {parentOrgId && activeChildHOAdmins?.length > 0 && (
        <div className={styles.adminSelect}>
          <label className={styles.adminLabel}>
            Assign Admin
          </label>
          <Select
            name="selectedAdmin"
            options={activeChildHOAdmins.map((admin) => ({
              value: admin.fullName,
              label: admin.id,
            }))}
            onChange={handleAdminChange}
          />
        </div>
      )}

      {shouldShowFeatures && (
        <>
          <div className={styles.enterpriseFeaturesHeader}>
            <span className={styles.enterpriseFeaturesTitle}>Additional Features</span>
          </div>
          <div className={styles.enterpriseFeaturesContainer}>
            <div className={styles.featureRow}>
              <div className={styles.featureInfo}>
                <div className={styles.featureIconWrapper}>
                  <img src={UberCarIcon} alt="Uber Health" className={styles.featureIcon} />
                </div>
                <div>
                  <div className={styles.featureName}>Uber Health</div>
                  <div className={styles.featureDescription}>
                    Enable Uber Health services for this organization
                  </div>
                </div>
              </div>
              <div className={styles.featureControl}>
                <Switcher
                  name="addons.uberHealthEnabled"
                  value={!!values?.addons?.uberHealthEnabled}
                  onChange={({ setFieldValue, value }) => {
                    setFieldValue('addons.uberHealthEnabled', value);
                    if (value) {
                      const current = values?.addons?.uberHealthSurcharge;
                      setFieldValue(
                        'addons.uberHealthSurcharge',
                        typeof current === 'number' ? current : 0,
                      );
                    } else {
                      setFieldValue('addons.uberHealthSurcharge', 0);
                    }
                  }}
                />
              </div>
            </div>

            {values?.addons?.uberHealthEnabled && (
              <div className={styles.uberSurchargeRow}>
                <div className={styles.uberSurchargeLabel}>Uber Health Surcharge</div>
                <div className={styles.uberSurchargeInput}>
                  <Input
                    name="addons.uberHealthSurcharge"
                    type="number"
                    min={0}
                    max={99}
                    precision={0}
                    prefix="$"
                  />
                </div>
              </div>
            )}

            {/* MedHaul Insights - Enterprise only */}
            {shouldShowEnterpriseOnlyFeatures && (
              <div className={styles.featureRow}>
                <div className={styles.featureInfo}>
                  <div className={styles.featureIconWrapper}>
                    <img src={InsightsHOIcon} alt="MedHaul Insights" className={styles.featureIcon} />
                  </div>
                  <div>
                    <div className={styles.featureName}>MedHaul Insights</div>
                    <div className={styles.featureDescription}>
                      Advanced analytics and reporting for organization rides and usage
                    </div>
                  </div>
                </div>
                <div className={styles.featureControl}>
                  <Switcher
                    name="addons.insightsEnabled"
                    disabled={values?.isEnterprise && disableEnterpriseCheckbox}
                    value={!!values?.addons?.insightsEnabled}
                    onChange={({ setFieldValue, value }) => {
                      setFieldValue('addons.insightsEnabled', value);
                    }}
                  />
                </div>
              </div>
            )}

            {/* Gemini (Codi) - Enterprise only */}
            {shouldShowEnterpriseOnlyFeatures && (
              <div className={styles.featureRow}>
                <div className={styles.featureInfo}>
                  <div className={styles.featureIconWrapper}>
                    <img src={GeminiIcon} alt="Gemini" className={styles.featureIcon} />
                  </div>
                  <div>
                    <div className={styles.featureName}>Gemini</div>
                    <div className={styles.featureDescription}>
                      Seamless integration of Gemini AI with Kodi.
                    </div>
                  </div>
                </div>
                <div className={styles.featureControl}>
                  <Switcher
                    name="addons.codiEnabled"
                    value={!!values?.addons?.codiEnabled}
                    onChange={({ setFieldValue, value }) => {
                      setFieldValue('addons.codiEnabled', value);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {(!parentOrgId || (parentOrgId && activeChildHOAdmins?.length === 0)) && (
        <>
          <div className={styles.name}>
            <label className={styles.nameLabel}>
              <span className={parentOrgId ? '' : styles.required}>Administrator's First Name</span>
              <Input
                name="admin.firstName"
                type="text"
                placeholder="Administrator's First Name"
                disabled={personalInfoFieldsDisabled}
              />
            </label>
            <label className={styles.nameLabel}>
              <span className={parentOrgId ? '' : styles.required}>Administrator's Last Name</span>
              <Input
                name="admin.lastName"
                type="text"
                placeholder="Administrator's Last Name"
                disabled={personalInfoFieldsDisabled}
              />
            </label>
          </div>

          <div className={styles.emailContainer}>
            <label className={styles.email}>
              <span className={parentOrgId ? '' : styles.required}> Admin's Email</span>
              <Input
                name="admin.email"
                type="text"
                placeholder="Admin's Email"
                disabled={personalInfoFieldsDisabled}
              />
            </label>
            {isTopLevelOrg && switchAdmin && (
              <div className={styles.changeAdminBtn}>
                <Button
                  myClassName={styles.formControlBtn}
                  onClick={handleChangeAdmin}
                >
                  Switch Admin
                </Button>
              </div>
            )}
            {!isTopLevelOrg && switchAdmin && (
              <div className={styles.changeAdminBtn}>
                <Button
                  myClassName={styles.formControlBtn}
                  onClick={handleAssignAdmin}>
                  Assign Admin
                </Button>
              </div>
            )}
          </div>
        </>)}

      {switchAdmin && !parentOrgId && (
        <div className={styles.inviteBtn}>
          <Button
            myClassName={styles.formControlBtn}
            onClick={handleInviteUser}
          >
            Invite New User
          </Button>
        </div>
      )}

      <div>
        <div className={styles.checkboxesColumn}>
          <label className={styles.rideRequestFieldsHead}>
            Included Ride Request fields
          </label>
          {values && RideRequestFields(values, false, rest.setValues)}
        </div>
      </div>
    </>
  );
};
